import * as React from 'react';
import { Responsive } from 'vendors/semantic-ui';

import { buildCMSStyles, getDefault, readCMSValue } from 'modules/shared-cms/utils';
import { getNestedReferenceId } from 'modules/shared-cms-advanced/utils';

import { Actualite, Carrousel, Introduction, Promo } from 'modules/shared-cms-advanced/enhanced-blocs';
import { NouveautesCatalogue, TendanceCatalogue } from 'modules/shared-cms-catalogue';

import { DescriptionContainer, ImagesListBloc, LogosBloc } from 'modules/shared-cms-advanced';
import { CtaButton, LightSeparator } from 'modules/shared-ui/components';
import { TitleProps } from 'modules/shared-cms/cms-base-type';
import { configUI } from 'config';
import { HomeAltProps } from './home-alt-type';
import enhancer from './home-alt-enhancer';
import styles from './home-alt-page.module.css';

const InnerHomeAltPage: React.FC<HomeAltProps> = React.memo(
    ({
        intro,
        carrousel,
        nouveautesCatalogue,
        universeTitle,
        universeFontSizeTitle,
        universeImageList,
        brandTitle,
        brandImageList,
        brandDescription,
        brandDescriptionFontSize,
        brandDescriptionTextColor,
        brandCtaText,
        brandCtaLink,
        brandCtaStyle,
        actualite,
        tendance,
        promo,
        ...rest
    }: HomeAltProps) => {
        const {
            introId = getDefault(getNestedReferenceId(intro), ''),
            carrouselId = getDefault(getNestedReferenceId(carrousel), ''),
            nouveautesCatalogueId = getDefault(getNestedReferenceId(nouveautesCatalogue), ''),
            universeBlockTitle = getDefault(readCMSValue(universeTitle), ''),
            universeBlockFontSizeTitle = getDefault(readCMSValue(universeFontSizeTitle), 30),
            universeImages = getDefault(readCMSValue(universeImageList), []),
            brandBlockTitle = getDefault(readCMSValue(brandTitle), ''),
            brandImages = getDefault(readCMSValue(brandImageList), []),
            brandDescriptionProps = {
                description: getDefault(readCMSValue(brandDescription), ''),
                descriptionFontSize: getDefault(readCMSValue(brandDescriptionFontSize), 18),
                descriptionTextColor: getDefault(readCMSValue(brandDescriptionTextColor), '#000000'),
            },
            brandCtaProps = {
                ctaText: getDefault(readCMSValue(brandCtaText), ''),
                ctaLink: getDefault(readCMSValue(brandCtaLink), ''),
                ctaStyle: getDefault(readCMSValue(brandCtaStyle), 'normal'),
            },
            actualiteId = getDefault(getNestedReferenceId(actualite), ''),
            tendanceId = getDefault(getNestedReferenceId(tendance), ''),
            promoId = getDefault(getNestedReferenceId(promo), ''),
        } = {};

        const universeBlockTitleStyle = buildCMSStyles<TitleProps>({
            title: universeBlockTitle,
            fontSizeTitle: universeBlockFontSizeTitle,
        });

        return (
            <div className={styles.rootContainer}>
                {introId !== '' && <Introduction id={introId} fullWidth />}

                {carrouselId !== '' && (
                    <Carrousel id={carrouselId} placeholderSize="large" compactResponsive largeLegend />
                )}

                <Responsive as={React.Fragment} maxWidth={configUI.responsive.mobile.maxWidth}>
                    <div className={styles.separator}>
                        <LightSeparator />
                    </div>
                </Responsive>
                {nouveautesCatalogueId !== '' && <NouveautesCatalogue id={nouveautesCatalogueId} />}

                <div className={styles.separator}>
                    <LightSeparator />
                </div>
                <div className={styles.universeBlock}>
                    <span className={styles.universeBlockTitle} style={universeBlockTitleStyle}>
                        {universeBlockTitle}
                    </span>
                    <DescriptionContainer {...rest} />
                    {universeImages.length > 0 && (
                        <div className={styles.imageListContainer}>
                            <ImagesListBloc
                                list={universeImages}
                                style4Items
                                fullWidth
                                captionClassName={styles.universeItemCaption}
                            />
                        </div>
                    )}
                </div>

                <div className={styles.separator}>
                    <LightSeparator />
                </div>
                <div className={styles.brandBlock}>
                    <span className={styles.brandBlockTitle}>{brandBlockTitle}</span>
                    {brandImages.length > 0 && (
                        <div>
                            <LogosBloc logos={brandImages} />
                        </div>
                    )}
                    <DescriptionContainer {...brandDescriptionProps} />
                    {brandCtaProps.ctaText !== '' && (
                        <div className={styles.ctaContainer}>
                            <CtaButton {...brandCtaProps} />
                        </div>
                    )}
                </div>

                <div className={styles.separator}>
                    <LightSeparator />
                </div>
                {tendanceId !== '' && <TendanceCatalogue id={tendanceId} />}

                <div className={styles.separator}>
                    <LightSeparator />
                </div>
                {actualiteId !== '' && <Actualite id={actualiteId} />}

                <div className={styles.separator}>
                    <LightSeparator />
                </div>
                {promoId ? <Promo id={promoId} /> : ''}
            </div>
        );
    },
);

export const HomeAltPage = enhancer(InnerHomeAltPage);
